/**
 * Formata texto com marcadores do WhatsApp para exibição em HTML.
 * Regras: *negrito* | _itálico_ | ~tachado~ | ```monoespaço``` | linhas com "> " (citação)
 * Texto é escapado para XSS; HTML legado rico (links, span, tabelas…) é preservado sanitizado.
 *
 * APIs (ex. API Fácil) costumam gravar texto puro dentro de <p>, <div> ou com <br>.
 * Isso não deve impedir a conversão dos marcadores do WhatsApp.
 */

const escapeHtml = (str) => {
  if (str == null || typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const sanitizeLegacyHtml = (html) => {
  if (html == null || typeof html !== "string") return "";

  let cleaned = html;

  cleaned = cleaned.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
  cleaned = cleaned.replace(/\s+on\w+="[^"]*"/gi, "");
  cleaned = cleaned.replace(/\s+on\w+='[^']*'/gi, "");
  cleaned = cleaned.replace(/javascript:/gi, "");

  return cleaned;
};

/** Tags que indicam conteúdo HTML real (não só invólucro de parágrafo/quebra). */
const RICH_HTML_TAG =
  /<\/?(?:a|span|table|tbody|thead|tr|td|th|img|video|iframe|svg|path|style|script|link|strong|b|em|i|u|font|h[1-6]|ul|ol|li|blockquote|pre|button|form|input|label)\b/i;

/**
 * Remove invólucros triviais deixando texto + quebras (para aplicar marcadores WA).
 */
function unwrapBenignHtmlWrappers(s) {
  let t = String(s).replace(/\r\n/g, "\n");
  t = t.replace(/<br\s*\/?>/gi, "\n");
  t = t.replace(/<\/p>\s*<p(\s[^>]*)?>/gi, "\n");
  t = t.replace(/<p(\s[^>]*)?>/gi, "");
  t = t.replace(/<\/p>/gi, "\n");
  t = t.replace(/<div(\s[^>]*)?>/gi, "");
  t = t.replace(/<\/div>/gi, "\n");
  return t.trim();
}

/** Ainda há alguma tag HTML (após tirar p/div/br)? */
function hasAnyTagOpen(s) {
  return /<[a-z!?]/i.test(s);
}

const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"{}|\\^`[\]]+/gi;

function trimUrlTrailingPunctuation(url) {
  let trimmed = url;
  let suffix = "";

  while (/[.,!?;:]$/.test(trimmed)) {
    suffix = trimmed.slice(-1) + suffix;
    trimmed = trimmed.slice(0, -1);
  }

  while (/\)$/.test(trimmed)) {
    const openCount = (trimmed.match(/\(/g) || []).length;
    const closeCount = (trimmed.match(/\)/g) || []).length;
    if (closeCount <= openCount) break;
    suffix = ")" + suffix;
    trimmed = trimmed.slice(0, -1);
  }

  while (/\]$/.test(trimmed)) {
    const openCount = (trimmed.match(/\[/g) || []).length;
    const closeCount = (trimmed.match(/\]/g) || []).length;
    if (closeCount <= openCount) break;
    suffix = "]" + suffix;
    trimmed = trimmed.slice(0, -1);
  }

  return { url: trimmed, suffix };
}

function linkifyUrls(text) {
  if (text == null || typeof text !== "string" || !text) return text;

  return text.replace(URL_PATTERN, (match) => {
    const { url, suffix } = trimUrlTrailingPunctuation(match);
    if (!url) return match;

    const href = /^www\./i.test(url) ? `https://${url}` : url;
    const hrefAttr = href.replace(/&amp;/g, "&").replace(/&quot;/g, '"');

    return `<a href="${hrefAttr}" target="_blank" rel="noopener noreferrer">${url}</a>${suffix}`;
  });
}

function applyWhatsappMarkers(escaped) {
  const codeBlocks = [];
  let out = escaped.replace(/```([\s\S]*?)```/g, (_, code) => {
    const token = `\x00CODE${codeBlocks.length}\x00`;
    codeBlocks.push(`<code>${code}</code>`);
    return token;
  });

  out = linkifyUrls(out);

  codeBlocks.forEach((block, index) => {
    out = out.replace(`\x00CODE${index}\x00`, block);
  });

  out = out.replace(
    /^&gt;\s?(.*)$/gm,
    '<span style="display:block;border-left:3px solid currentColor;padding-left:8px;margin:4px 0;opacity:0.92">$1</span>'
  );
  out = out.replace(/_([\s\S]*?)_/g, "<em>$1</em>");
  out = out.replace(/\*([\s\S]*?)\*/g, "<strong>$1</strong>");
  out = out.replace(/~([\s\S]*?)~/g, "<s>$1</s>");
  return out;
}

/** Texto plano da anotação (para medir tamanho / preview). */
export function getInternalNotePlainText(text) {
  if (text == null || typeof text !== "string") return "";
  const unwrapped = unwrapBenignHtmlWrappers(String(text));
  return unwrapped
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\r\n/g, "\n")
    .trim();
}

export function shouldCollapseInternalNote(text, { maxChars = 380, maxLines = 6 } = {}) {
  const plain = getInternalNotePlainText(text);
  if (!plain) return false;
  const lineCount = plain.split("\n").length;
  return plain.length > maxChars || lineCount > maxLines;
}

/**
 * HTML para anotações internas: marcadores WA + quebras de linha preservadas.
 */
export function formatInternalNoteHtml(text) {
  if (text == null || typeof text !== "string") return "";

  const raw = String(text);
  const unwrapped = unwrapBenignHtmlWrappers(raw);

  if (RICH_HTML_TAG.test(unwrapped)) {
    return sanitizeLegacyHtml(raw);
  }

  if (hasAnyTagOpen(unwrapped)) {
    return sanitizeLegacyHtml(raw);
  }

  let escaped = escapeHtml(unwrapped);
  escaped = escaped.replace(/\n/g, "<br />");
  return applyWhatsappMarkers(escaped);
}

/**
 * @param {string} text - Texto da mensagem (marcadores WA e/ou HTML)
 * @returns {string} HTML seguro para dangerouslySetInnerHTML
 */
export function formatWhatsAppText(text) {
  if (text == null || typeof text !== "string") return "";

  const raw = String(text);
  const unwrapped = unwrapBenignHtmlWrappers(raw);

  if (RICH_HTML_TAG.test(unwrapped)) {
    return sanitizeLegacyHtml(raw);
  }

  if (hasAnyTagOpen(unwrapped)) {
    return sanitizeLegacyHtml(raw);
  }

  const escaped = escapeHtml(unwrapped);
  return applyWhatsappMarkers(escaped);
}
